import {
  AdmCompany,
  AdmProject,
  AllMemberTypes,
} from "@custom-types/base-types";
import { RequirePermission } from "@components/common/require-permission";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { GeneralProfileEvents } from "@utils/track-event/track-event-list";
import { useSubjectClipboard } from "@hooks/use-subject-clipboard";
import { Button, useToast } from "@flat-ui";
import { LocationProp } from "@utils/track-event/event-types";

interface CompanyProps {
  /** Subject type equal to company so that entity type can be easily understand as AdmCompany */
  subjectType: "COMPANY";

  /** Type of entity */
  entity: AdmCompany;
}

interface ProjectProps {
  /** Subject type equal to project so that entity type can be easily understand as AdmProject */
  subjectType: "PROJECT";

  /** Type of entity */
  entity: AdmProject;
}

interface MemberProps {
  /** Subject type equal to user so that entity type can be easily understand as AllMemberTypes */
  subjectType: "USER";

  /** Type of entity */
  entity: AllMemberTypes;
}

type Props = CompanyProps | ProjectProps | MemberProps;

/** Button component for copying profile information to clipboard */
export function ProfileInfoCopyButton({
  subjectType,
  entity,
}: Props): React.JSX.Element {
  const { openToast } = useToast();
  const { trackEvent } = useTrackEvent();
  const { copyCompanyInfo, copyProjectInfo, copyMemberInfo } =
    useSubjectClipboard();

  async function onClick(): Promise<void> {
    function getLocation(): LocationProp["location"] {
      switch (subjectType) {
        case "COMPANY":
          return "workspace profile info tab";
        case "PROJECT":
          return "project profile info tab";
        case "USER":
          return "member profile info tab";
      }
    }

    trackEvent({
      name: GeneralProfileEvents.copyDetailsToClipboard,
      props: {
        target: subjectType,
        location: getLocation(),
      },
    });

    switch (subjectType) {
      case "COMPANY":
        await copyCompanyInfo(entity);
        openToast({
          title: "Workspace info copied to clipboard!",
        });
        break;
      case "PROJECT":
        await copyProjectInfo(entity);
        openToast({
          title: "Project info copied to clipboard!",
        });
        break;
      case "USER":
        await copyMemberInfo(entity);
        openToast({
          title: "Member info copied to clipboard!",
        });
        break;
    }
  }

  const copyButton = (
    <Button onClick={onClick} variant="secondary">
      Copy details to clipboard
    </Button>
  );

  return (
    <RequirePermission permission="general.copyDetailsToClipboard">
      {copyButton}
    </RequirePermission>
  );
}
